import { Link } from "react-router-dom";
import { ArrowRight, Scale } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { COMPARISONS } from "../../data/compareData";

export default function CompareIndexPage() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Compare", path: "/compare" },
  ];
  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="compare-index-page">
      <Seo
        title="Compare Fieldz AI to Other Field Service Software"
        description="Honest, feature-by-feature comparisons of Fieldz AI vs Jobber, Housecall Pro, ServiceTitan, and Workiz — phone systems, commissions, pricing, and more."
        path="/compare"
        image={`${SITE_URL}/marketing/og/og-compare.jpg`}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <MarketingNav />

      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[var(--mk-blue)] mb-5">Comparisons</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
            How Fieldz AI stacks up
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Feature-by-feature comparisons against the platforms field service businesses evaluate most — with the differences that actually matter.
          </p>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {COMPARISONS.map((c, i) => (
              <Reveal key={c.slug} delay={Math.min((i % 2) + 1, 2)}>
                <Link
                  to={`/compare/${c.slug}`}
                  className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)] hover:border-blue-200"
                  data-testid={`compare-index-card-${c.slug}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[var(--mk-blue-light)] flex items-center justify-center mb-4">
                    <Scale className="w-5 h-5 text-[var(--mk-blue)]" />
                  </div>
                  <h2 className="text-[18px] font-semibold text-slate-900 mb-2">Fieldz AI vs {c.competitor}</h2>
                  <p className="text-[14px] text-slate-500 leading-relaxed mb-4 line-clamp-3">{c.intro}</p>
                  <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-[var(--mk-blue)]">
                    Read the comparison <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title="The best comparison is a week of real jobs" body="Start the free trial and test Fieldz AI on your actual workflow — no credit card required." />
      <MarketingFooter />
    </div>
  );
}
